import React, { useEffect, useRef, useState } from 'react'

import SafetySystemsModal from './SafetySystemsModal'

// The ServiceModule stores this sensor as the singular "Diagnostic Scan", but
// the approved designs show it pluralized ("Diagnostic Scans") in both the
// section header and the "… Insights" toggle. The DB value is shared widely and
// must not change, so pluralize at render time only.
const SENSOR_DISPLAY_NAMES = {
  'Diagnostic Scan': 'Diagnostic Scans',
}
const displaySensorName = (name) => (name && SENSOR_DISPLAY_NAMES[name]) || name

const StatusBadge = ({ status }) => {
  if (!status) return null
  const isRequired = status === 'required'
  return (
    <span className={`adas-detail__status-badge ${isRequired ? 'required' : 'potential'}`}>
      {isRequired ? 'Required' : 'Potential'}
    </span>
  )
}

const SensorInsights = ({ sensorId, sensorInsights, forceOpen }) => {
  const [open, setOpen] = useState(false)
  const isOpen = forceOpen || open
  const insight = (sensorInsights || []).find(s => s.sensorId === sensorId)
  if (!insight || !insight.conditions || !insight.conditions.length) return null

  return (
    <div className='adas-detail__insights'>
      <button className='adas-detail__insights-toggle' onClick={() => setOpen(!open)}>
        {displaySensorName(insight.sensorName || 'Sensor')} Insights {isOpen ? '▲' : '▼'}
      </button>
      {isOpen && (
        <div className='adas-detail__insights-content'>
          {insight.conditions.map((cond, i) => (
            <div key={i} className='adas-detail__insight-condition'>
              <span className='adas-detail__insight-component'>{cond.component}</span>
              {cond.items && cond.items.map((item, j) => (
                <div key={j} className='adas-detail__insight-item'>
                  <span>{item.label}</span>
                  {item.lineReferences && <span className='adas-detail__insight-ref'> — {item.lineReferences}</span>}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const formatPrice = (amount) => {
  return `$ ${(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

const normalizeServiceDisplayName = (name) => {
  if (!name) return ''
  const rawName = String(name).trim()
  if (!rawName) return ''
  const stripped = rawName
    .replace(/\s*[-–]?\s*\(?(oem|aftermarket)\)?\s*$/i, '')
    .trim()
  return stripped || rawName
}

const menuStyles = {
  menu: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: 4,
    background: '#fff',
    border: '1px solid #E0E6EB',
    borderRadius: 8,
    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
    zIndex: 10,
    minWidth: 140,
    overflow: 'hidden',
  },
  item: {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    background: 'none',
    border: 'none',
    padding: '10px 16px',
    fontSize: 14,
    color: '#2A2A2A',
    cursor: 'pointer',
  },
}

const AdasSections = ({ report, allowViewPricing }) => {
  const [expandAll, setExpandAll] = useState(false)
  const [showSafety, setShowSafety] = useState(false)
  const [showMenu, setShowMenu] = useState(false)
  const menuRef = useRef(null)
  const services = (report.adas && report.adas.services) || []

  useEffect(() => {
    if (!showMenu) return undefined
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showMenu])
  const sensorInsights = report.sensorInsights || []
  const collision = report.adas && report.adas.vinDecodeData && report.adas.vinDecodeData.collision

  if (!services.length) {
    return <p style={{ color: '#69787F', marginTop: 24 }}>No ADAS services on this report.</p>
  }

  // Group by categoryName, then by sensorName
  const categories = {}
  services.forEach(svc => {
    const cat = svc.categoryName || 'Uncategorized'
    if (!categories[cat]) categories[cat] = {}
    const sensor = svc.sensorName || 'General'
    if (!categories[cat][sensor]) categories[cat][sensor] = { services: [], sensorId: svc.rawSensorId || svc.sensorId }
    categories[cat][sensor].services.push(svc)
  })

  return (
    <div className='adas-detail__report-card'>
      <div className='adas-detail__report-header'>
        <div className='adas-detail__report-header-left'>
          <h3 className='adas-detail__report-title'>ADAS Report</h3>
          <label className='adas-detail__collision-check'>
            <input type='checkbox' checked={!!collision} readOnly />
            <span>Vehicle was involved in a collision</span>
          </label>
        </div>
        <div className='adas-detail__report-header-right' style={{ position: 'relative' }} ref={menuRef}>
          <button className='adas-detail__btn-safety' onClick={() => setShowSafety(true)}>Safety Systems</button>
          <button className='adas-detail__btn-more' onClick={() => setShowMenu(v => !v)}>⋯</button>
          {showMenu && (
            <div className='adas-detail__more-menu' style={menuStyles.menu}>
              <button
                style={menuStyles.item}
                onClick={() => { setExpandAll(true); setShowMenu(false) }}
              >
                Expand All
              </button>
              <button
                style={menuStyles.item}
                onClick={() => { setExpandAll(false); setShowMenu(false) }}
              >
                Collapse All
              </button>
            </div>
          )}
        </div>
      </div>

      <div>
        {Object.entries(categories).map(([catName, sensors]) => (
          <div key={catName}>
            <div className='adas-detail__category-row'>{catName}</div>

            {Object.entries(sensors).map(([sensorName, { services: sensorServices, sensorId }]) => (
              <div key={sensorName}>
                <div className='adas-detail__sensor-header'>{displaySensorName(sensorName)}</div>

                {sensorServices.map(svc => (
                  <div key={svc._id} className='adas-detail__service-row'>
                    <div className='adas-detail__service-name'>
                      <span>{normalizeServiceDisplayName(svc.operation ? svc.operation.name : '') || '—'}</span>
                      {svc.calibrationType && (
                        <span className='calibration-type'>({svc.calibrationType})</span>
                      )}
                    </div>
                    <div className='adas-detail__service-status'>
                      <span className='adas-detail__meta-label'>Status:</span>
                      <StatusBadge status={svc.recommendedStatus} />
                    </div>
                    {allowViewPricing && (
                      <div className='adas-detail__service-price'>
                        <span className='adas-detail__meta-label'>Price:</span>
                        <span className='adas-detail__price-value'>{formatPrice(svc.amount)}</span>
                      </div>
                    )}
                    <span className='adas-detail__service-more'>⋯</span>
                  </div>
                ))}

                <SensorInsights sensorId={sensorId} sensorInsights={sensorInsights} forceOpen={expandAll} />
              </div>
            ))}
          </div>
        ))}
      </div>

      <SafetySystemsModal show={showSafety} onHide={() => setShowSafety(false)} report={report} />
    </div>
  )
}

export default AdasSections
